import { useState } from 'react';
import { ApiError } from '../api/client';
import { useIssueToken, useRevokeToken } from '../api/queries';
import { isPlayerVerified, type User } from '../api/types';
import { useToast } from './Toast';

function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleString('de-DE') : '—';
}

/**
 * Verwaltung des Usertokens. Der Klartext lebt nur in diesem Zustand und
 * verschwindet mit dem Verlassen der Seite — genau wie serverseitig.
 */
export function TokenSection({ user }: { user: User }) {
  const issue = useIssueToken();
  const revoke = useRevokeToken();
  const toast = useToast();
  const [freshToken, setFreshToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const busy = issue.isPending || revoke.isPending;
  const error = issue.error ?? revoke.error;

  const create = (replacing: boolean) => {
    if (
      replacing &&
      !window.confirm(
        'Neuen Token erzeugen? Der bisherige wird sofort ungültig — ein Userscript, das ihn nutzt, verliert die Verbindung.',
      )
    ) {
      return;
    }

    issue.mutate(undefined, {
      onSuccess: (data) => {
        setFreshToken(data.token);
        setCopied(false);
        toast.push('success', replacing ? 'Neuer Token erzeugt.' : 'Token erzeugt.');
      },
      onError: (issueError) => toast.push('error', issueError.message),
    });
  };

  const drop = () => {
    if (!window.confirm('Token widerrufen? Verbundene Userscripts verlieren sofort den Zugriff.')) {
      return;
    }

    revoke.mutate(undefined, {
      onSuccess: () => {
        setFreshToken(null);
        toast.push('success', 'Token wurde widerrufen.');
      },
      onError: (revokeError) => toast.push('error', revokeError.message),
    });
  };

  const copy = async () => {
    if (!freshToken) return;
    try {
      await navigator.clipboard.writeText(freshToken);
      setCopied(true);
      toast.push('success', 'Token in die Zwischenablage kopiert.');
    } catch {
      toast.push('info', 'Kopieren nicht möglich — bitte den Token von Hand markieren.');
    }
  };

  return (
    <section className="card">
      <h2>Usertoken</h2>
      <p className="muted">
        Mit diesem Token verbindet das Tampermonkey-Userscript dein Spiel mit dem Tool. Er
        gehört zu deinem Konto
        {user.world ? ` in der Welt „${user.world.name}"` : ''} — behandle ihn wie ein Passwort.
      </p>

      {error instanceof ApiError && <div className="flash flash-error">{error.message}</div>}

      {freshToken && (
        <div className="token-reveal">
          <p className="token-warn">
            <strong>Nur jetzt sichtbar.</strong> Der Token wird nicht im Klartext gespeichert und
            lässt sich später nicht erneut anzeigen — nur neu erzeugen.
          </p>
          <div className="token-value">
            <code>{freshToken}</code>
            <button type="button" className="btn btn-sm" onClick={copy}>
              {copied ? 'Kopiert ✓' : 'Kopieren'}
            </button>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setFreshToken(null)}
          >
            Verstanden — ausblenden
          </button>
        </div>
      )}

      {user.token ? (
        <>
          <dl className="meta-grid">
            <div>
              <dt>Token</dt>
              <dd>
                <code>{user.token.prefix}…</code>
              </dd>
            </div>
            <div>
              <dt>Erzeugt am</dt>
              <dd>{formatDate(user.token.createdAt)}</dd>
            </div>
            <div>
              <dt>Zuletzt genutzt</dt>
              <dd>{user.token.lastUsedAt ? formatDate(user.token.lastUsedAt) : 'noch nie'}</dd>
            </div>
          </dl>

          <div className="button-row">
            <button type="button" className="btn" disabled={busy} onClick={() => create(true)}>
              {issue.isPending ? 'Wird erzeugt …' : 'Neu erzeugen'}
            </button>
            <button type="button" className="btn btn-danger" disabled={busy} onClick={drop}>
              Widerrufen
            </button>
          </div>
          <p className="hint">
            Token verloren? „Neu erzeugen" ist der einzige Weg — der alte wird dabei ungültig.
          </p>
        </>
      ) : (
        <>
          {!isPlayerVerified(user) && (
            <p className="hint">
              Erst nach der Spielfigur-Verifizierung möglich — siehe oben.
            </p>
          )}
          <div className="button-row">
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy || user.status !== 'active' || !isPlayerVerified(user)}
              onClick={() => create(false)}
            >
              {issue.isPending ? 'Wird erzeugt …' : 'Token erzeugen'}
            </button>
          </div>
        </>
      )}

      <details className="howto">
        <summary>Wie das Userscript den Token nutzt</summary>
        <p className="muted">
          Das Skript schickt den Token als <code>Authorization</code>-Header an{' '}
          <code>/api/game/…</code>. Zum Prüfen der Verbindung:
        </p>
        <pre className="code-block">{`GM_xmlhttpRequest({
  method: 'GET',
  url: '${window.location.origin}/api/game/me',
  headers: { Authorization: 'Bearer ' + TOKEN },
  onload: (r) => console.log(JSON.parse(r.responseText)),
});`}</pre>
        <p className="muted">
          Die Antwort nennt Konto und Welt. Cookies spielen dort keine Rolle — der Token allein
          entscheidet.
        </p>
      </details>
    </section>
  );
}
